import React, { useState, useEffect } from "react";
import {
  supabase,
  Communication,
  CommunicationComment,
  Tables,
} from "../utils/supabase";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import {
  PlusIcon,
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MegaphoneIcon,
  AcademicCapIcon,
  CogIcon,
  XMarkIcon,
  EyeIcon,
  ChatBubbleOvalLeftIcon,
} from "@heroicons/react/24/outline";
import { format, formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";
import Pagination from "../components/Pagination";

const COMMS_PAGE_SIZE = 20;

interface CommunicationWithDetails extends Communication {
  created_by_name: string;
  pharmacy_name?: string;
  read_count: number;
  comment_count: number;
  scope_display: string;
  is_read?: boolean;
}

interface CommunicationFilters {
  type: string;
  category: string;
  scope: string;
  search: string;
}

const Communications: React.FC = () => {
  const { user, selectedPharmacy } = useAuth();
  const [communications, setCommunications] = useState<
    CommunicationWithDetails[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedCommunication, setSelectedCommunication] =
    useState<CommunicationWithDetails | null>(null);
  const [comments, setComments] = useState<
    (CommunicationComment & { user_name: string })[]
  >([]);
  const [newComment, setNewComment] = useState("");
  const [filters, setFilters] = useState<CommunicationFilters>({
    type: "all",
    category: "all",
    scope: "all",
    search: "",
  });
  const [page, setPage] = useState(1);

  const [newCommunication, setNewCommunication] = useState({
    title: "",
    message: "",
    type: "info" as
      | "info"
      | "action_required"
      | "urgent"
      | "announcement"
      | "policy"
      | "emergency"
      | "training"
      | "system",
    category: "general" as
      | "general"
      | "policy"
      | "emergency"
      | "training"
      | "system"
      | "announcement",
    is_company_wide: false,
    is_pinned: false,
    expires_at: "",
  });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchCommunications();
  }, [selectedPharmacy, filters]);

  const fetchCommunications = async () => {
    if (!selectedPharmacy && !isAdmin) {
      return;
    }

    try {
      setLoading(true);

      // Build query for communications
      let query = supabase
        .from("communication_details_enhanced")
        .select("*")
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: false });

      // Filter by pharmacy (include company-wide for all users)
      if (selectedPharmacy) {
        query = query.or(
          `pharmacy_id.eq.${selectedPharmacy.id},is_company_wide.eq.true`,
        );
      } else if (isAdmin) {
        // Admin sees everything - no additional filtering needed
      }

      // Apply filters
      if (filters.type !== "all") {
        query = query.eq("type", filters.type);
      }
      if (filters.category !== "all") {
        query = query.eq("category", filters.category);
      }
      if (filters.scope === "company-wide") {
        query = query.eq("is_company_wide", true);
      } else if (filters.scope === "pharmacy" && selectedPharmacy) {
        query = query.eq("pharmacy_id", selectedPharmacy.id);
      }
      if (filters.search) {
        query = query.or(
          `title.ilike.%${filters.search}%,message.ilike.%${filters.search}%`,
        );
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching communications:", error);
        toast.error("Failed to load communications");
        return;
      }

      // Check read status for each communication
      if (user && data) {
        const commIds = data.map((c) => c.id);
        const { data: readData } = await supabase
          .from(Tables.COMMUNICATION_READS)
          .select("communication_id")
          .eq("user_id", user.id)
          .in("communication_id", commIds);

        const readIds = new Set(readData?.map((r) => r.communication_id) || []);

        const communicationsWithReadStatus = data.map((comm) => ({
          ...comm,
          is_read: readIds.has(comm.id),
        }));

        setCommunications(communicationsWithReadStatus);
        setPage(1);
      }
    } catch (error) {
      console.error("Error fetching communications:", error);
      toast.error("Failed to load communications");
    } finally {
      setLoading(false);
    }
  };

  const createCommunication = async () => {
    if (!user || !newCommunication.title || !newCommunication.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!newCommunication.is_company_wide && !selectedPharmacy) {
      toast.error("Please select a pharmacy or create a company-wide message");
      return;
    }

    if (newCommunication.is_company_wide && !isAdmin) {
      toast.error("Only administrators can create company-wide messages");
      return;
    }

    try {
      const commData = {
        title: newCommunication.title,
        message: newCommunication.message,
        type: newCommunication.type,
        category: newCommunication.category,
        pharmacy_id: newCommunication.is_company_wide
          ? null
          : selectedPharmacy?.id,
        created_by: user.id,
        is_company_wide: newCommunication.is_company_wide,
        is_pinned: newCommunication.is_pinned,
        expires_at: newCommunication.expires_at || null,
      };

      const { error } = await supabase
        .from(Tables.COMMUNICATIONS)
        .insert(commData);

      if (error) {
        console.error("Error creating communication:", error);
        toast.error("Failed to create communication");
        return;
      }

      toast.success("Communication created successfully");
      setShowCreateModal(false);
      setNewCommunication({
        title: "",
        message: "",
        type: "info",
        category: "general",
        is_company_wide: false,
        is_pinned: false,
        expires_at: "",
      });
      fetchCommunications();
    } catch (error) {
      console.error("Error creating communication:", error);
      toast.error("Failed to create communication");
    }
  };

  const markAsRead = async (communicationId: string) => {
    if (!user) {
      return;
    }

    try {
      await supabase.from(Tables.COMMUNICATION_READS).upsert({
        communication_id: communicationId,
        user_id: user.id,
      });

      // Update local state
      setCommunications((prev) =>
        prev.map((comm) =>
          comm.id === communicationId
            ? {
                ...comm,
                is_read: true,
                read_count: comm.read_count + (comm.is_read ? 0 : 1),
              }
            : comm,
        ),
      );
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const fetchComments = async (communicationId: string) => {
    try {
      const { data, error } = await supabase
        .from(Tables.COMMUNICATION_COMMENTS)
        .select(
          `
          *,
          user:users(full_name)
        `,
        )
        .eq("communication_id", communicationId)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching comments:", error);
        return;
      }

      const commentsWithNames =
        data?.map((comment) => ({
          ...comment,
          user_name: comment.user?.full_name || "Unknown User",
        })) || [];

      setComments(commentsWithNames);
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const addComment = async () => {
    if (!user || !selectedCommunication || !newComment.trim()) {
      return;
    }

    try {
      const { error } = await supabase
        .from(Tables.COMMUNICATION_COMMENTS)
        .insert({
          communication_id: selectedCommunication.id,
          user_id: user.id,
          comment: newComment.trim(),
        });

      if (error) {
        console.error("Error adding comment:", error);
        toast.error("Failed to add comment");
        return;
      }

      setNewComment("");
      fetchComments(selectedCommunication.id);
      fetchCommunications(); // Refresh to update comment count
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "urgent":
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case "announcement":
        return <MegaphoneIcon className="h-5 w-5 text-blue-500" />;
      case "policy":
        return <InformationCircleIcon className="h-5 w-5 text-yellow-500" />;
      case "emergency":
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />;
      case "training":
        return <AcademicCapIcon className="h-5 w-5 text-purple-500" />;
      case "system":
        return <CogIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <InformationCircleIcon className="h-5 w-5 text-primary-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "urgent":
        return "bg-red-100 text-red-800 border-red-200";
      case "announcement":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "policy":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "emergency":
        return "bg-red-100 text-red-900 border-red-300";
      case "training":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "system":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-primary-100 text-primary-800 border-primary-200";
    }
  };

  if (loading) {
    return (
      <div className="p-6 sm:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Communicatie</h1>
            <p className="text-gray-600 mt-1">
              Belangrijke informatie en bedrijfsbrede mededelingen
            </p>
            <p className="text-sm text-gray-500 mt-1">
              <strong>Verschil met Taken:</strong> Communicatie = "Dingen om te
              WETEN" • Taken = "Dingen om te DOEN"
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nieuwe Communicatie
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={filters.type}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, type: e.target.value }))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="all">Alle Types</option>
              <option value="info">Informatie</option>
              <option value="announcement">Aankondiging</option>
              <option value="policy">Beleid</option>
              <option value="urgent">Urgent</option>
              <option value="emergency">Noodgeval</option>
              <option value="training">Training</option>
              <option value="system">Systeem</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bereik
            </label>
            <select
              value={filters.scope}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, scope: e.target.value }))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="all">Alle Berichten</option>
              <option value="company-wide">Bedrijfsbreed</option>
              <option value="pharmacy">Alleen Deze Apotheek</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categorie
            </label>
            <select
              value={filters.category}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, category: e.target.value }))
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="all">Alle Categorieën</option>
              <option value="general">Algemeen</option>
              <option value="policy">Beleid</option>
              <option value="emergency">Noodgeval</option>
              <option value="training">Training</option>
              <option value="system">Systeem</option>
              <option value="announcement">Aankondiging</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Zoeken
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                placeholder="Zoek in berichten..."
                className="w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Communications Feed */}
      <div className="space-y-4">
        {communications.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Geen berichten
            </h3>
            <p className="text-gray-600">
              Er zijn nog geen communicaties voor de geselecteerde filters.
            </p>
          </div>
        ) : (
          communications.slice((page - 1) * COMMS_PAGE_SIZE, page * COMMS_PAGE_SIZE).map((comm) => (
            <div
              key={comm.id}
              className={`bg-white rounded-lg shadow-sm border-l-4 ${
                comm.is_pinned
                  ? "border-l-yellow-400 bg-yellow-50"
                  : comm.is_read
                    ? "border-l-gray-300"
                    : "border-l-primary-400 bg-primary-50"
              } p-6 hover:shadow-md transition-shadow cursor-pointer`}
              onClick={() => {
                if (!comm.is_read) {
                  markAsRead(comm.id);
                }
                setSelectedCommunication(comm);
                setShowCommentsModal(true);
                fetchComments(comm.id);
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    {getTypeIcon(comm.type)}
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeColor(comm.type)}`}
                    >
                      {comm.type}
                    </span>
                    {comm.is_company_wide && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        <GlobeAltIcon className="h-3 w-3 mr-1" />
                        Bedrijfsbreed
                      </span>
                    )}
                    {comm.is_pinned && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                        📌 Vastgepind
                      </span>
                    )}
                    {!comm.is_read && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 border border-primary-200">
                        Nieuw
                      </span>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {comm.title}
                  </h3>
                  <p className="text-gray-700 mb-3 line-clamp-3">
                    {comm.message}
                  </p>

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <span className="flex items-center">
                        <EyeIcon className="h-4 w-4 mr-1" />
                        {comm.read_count} gelezen
                      </span>
                      <span className="flex items-center">
                        <ChatBubbleOvalLeftIcon className="h-4 w-4 mr-1" />
                        {comm.comment_count} reacties
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span>{comm.scope_display}</span>
                      <span>•</span>
                      <span>{comm.created_by_name}</span>
                      <span>•</span>
                      <span>
                        {formatDistanceToNow(new Date(comm.created_at), {
                          addSuffix: true,
                          locale: nl,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        <Pagination
          page={page}
          pageSize={COMMS_PAGE_SIZE}
          total={communications.length}
          onPageChange={setPage}
        />
      </div>

      {/* Create Communication Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Nieuwe Communicatie Maken
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titel *
                    </label>
                    <input
                      type="text"
                      value={newCommunication.title}
                      onChange={(e) =>
                        setNewCommunication((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="Korte, duidelijke titel"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bericht *
                    </label>
                    <textarea
                      rows={4}
                      value={newCommunication.message}
                      onChange={(e) =>
                        setNewCommunication((prev) => ({
                          ...prev,
                          message: e.target.value,
                        }))
                      }
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder="Uitgebreide informatie die medewerkers moeten weten..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={newCommunication.type}
                        onChange={(e) =>
                          setNewCommunication((prev) => ({
                            ...prev,
                            type: e.target.value as
                              | "info"
                              | "action_required"
                              | "urgent"
                              | "announcement"
                              | "policy"
                              | "emergency"
                              | "training"
                              | "system",
                          }))
                        }
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      >
                        <option value="info">Informatie</option>
                        <option value="announcement">Aankondiging</option>
                        <option value="policy">Beleid</option>
                        <option value="urgent">Urgent</option>
                        <option value="emergency">Noodgeval</option>
                        <option value="training">Training</option>
                        <option value="system">Systeem</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Categorie
                      </label>
                      <select
                        value={newCommunication.category}
                        onChange={(e) =>
                          setNewCommunication((prev) => ({
                            ...prev,
                            category: e.target.value as
                              | "general"
                              | "policy"
                              | "emergency"
                              | "training"
                              | "system"
                              | "announcement",
                          }))
                        }
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      >
                        <option value="general">Algemeen</option>
                        <option value="policy">Beleid</option>
                        <option value="emergency">Noodgeval</option>
                        <option value="training">Training</option>
                        <option value="system">Systeem</option>
                        <option value="announcement">Aankondiging</option>
                      </select>
                    </div>
                  </div>

                  {isAdmin && (
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newCommunication.is_company_wide}
                          onChange={(e) =>
                            setNewCommunication((prev) => ({
                              ...prev,
                              is_company_wide: e.target.checked,
                            }))
                          }
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          <strong>Bedrijfsbrede communicatie</strong> (zichtbaar
                          voor alle apotheken)
                        </span>
                      </label>
                      <p className="mt-1 text-xs text-gray-500">
                        Alleen administrators kunnen bedrijfsbrede berichten
                        verzenden
                      </p>
                    </div>
                  )}

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newCommunication.is_pinned}
                        onChange={(e) =>
                          setNewCommunication((prev) => ({
                            ...prev,
                            is_pinned: e.target.checked,
                          }))
                        }
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Vastpinnen (bovenaan lijst)
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vervaldatum (optioneel)
                    </label>
                    <input
                      type="datetime-local"
                      value={newCommunication.expires_at}
                      onChange={(e) =>
                        setNewCommunication((prev) => ({
                          ...prev,
                          expires_at: e.target.value,
                        }))
                      }
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Berichten worden automatisch verborgen na vervaldatum
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={createCommunication}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Publiceren
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showCommentsModal && selectedCommunication && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 max-h-[90vh] overflow-y-auto">
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {selectedCommunication.title}
                  </h3>
                  <button
                    onClick={() => setShowCommentsModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Communication Details */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    {getTypeIcon(selectedCommunication.type)}
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeColor(selectedCommunication.type)}`}
                    >
                      {selectedCommunication.type}
                    </span>
                    {selectedCommunication.is_company_wide && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        <GlobeAltIcon className="h-3 w-3 mr-1" />
                        Bedrijfsbreed
                      </span>
                    )}
                    {selectedCommunication.is_pinned && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                        📌 Vastgepind
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 mb-3">
                    {selectedCommunication.message}
                  </p>
                  <div className="text-sm text-gray-500">
                    Door {selectedCommunication.created_by_name} •{" "}
                    {format(
                      new Date(selectedCommunication.created_at),
                      "d MMMM yyyy 'om' HH:mm",
                      { locale: nl },
                    )}
                  </div>
                </div>

                {/* Comments Section */}
                <div className="border-t pt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4">
                    Reacties ({comments.length})
                  </h4>

                  {/* Comments List */}
                  <div className="space-y-4 mb-6 max-h-60 overflow-y-auto">
                    {comments.length === 0 ? (
                      <p className="text-gray-500 text-sm italic">
                        Nog geen reacties. Wees de eerste!
                      </p>
                    ) : (
                      comments.map((comment) => (
                        <div
                          key={comment.id}
                          className="bg-gray-50 rounded-lg p-3"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900">
                              {comment.user_name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDistanceToNow(
                                new Date(comment.created_at),
                                { addSuffix: true, locale: nl },
                              )}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700">
                            {comment.comment}
                          </p>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Comment */}
                  <div>
                    <textarea
                      rows={3}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Voeg een reactie toe..."
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                    />
                    <div className="mt-2 flex justify-end">
                      <button
                        onClick={addComment}
                        disabled={!newComment.trim()}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Reactie Plaatsen
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Communications;
